/**
 * TaskGeometryOverlays Component
 *
 * Renders non-marker geometries (lines and shapes) for visible tasks.
 * Markers are handled by the Symbol Layer; this component handles
 * only Line and Shape geometries via existing WebGL renderers.
 *
 * Excludes the currently-edited task (rendered by CurrentGeometriesRenderer).
 *
 * @module components/map/UnifiedAnchorSymbolLayer/TaskGeometryOverlays
 */

import { memo } from 'react';

import { useGeoMarkupStore } from '@/store';
import { GeometryType } from '@/types/geometry';
import { LineRenderer } from '../GeometryRenderer/LineRenderer';
import { ShapeRenderer } from '../GeometryRenderer/ShapeRenderer';
import type { Task } from '@/types';

// ===== Types =====

interface TaskGeometryOverlaysProps {
  /** Array of tasks whose non-marker geometries should be rendered */
  tasks: Task[];
  /** Current map zoom level */
  zoom: number;
}

// ===== Component =====

/**
 * Renders Line and Shape geometries for visible tasks.
 *
 * Iterates each task's geometries array and renders:
 * - LineRenderer for GeometryType.Line
 * - ShapeRenderer for GeometryType.Shape
 * - Skips GeometryType.Marker (handled by UnifiedAnchorSymbolLayer)
 *
 * The currently-edited task is excluded to avoid duplicate rendering
 * with CurrentGeometriesRenderer.
 */
export const TaskGeometryOverlays = memo(function TaskGeometryOverlays({
  tasks,
  zoom,
}: TaskGeometryOverlaysProps) {
  // Determine which task is being edited (exclude from overlays)
  const editingTaskId = useGeoMarkupStore((state) =>
    state.tasks.formIsOpen ? state.tasks.selectedId : null
  );
  const selectedTaskId = useGeoMarkupStore((state) => state.tasks.selectedId);

  return (
    <>
      {tasks.map((task) => {
        // Skip the currently-edited task
        if (task.id === editingTaskId) return null;

        const isSelected = selectedTaskId === task.id;

        return task.geometries?.map((geometry) => {
          switch (geometry.type) {
            case GeometryType.Line:
              return (
                <LineRenderer
                  key={geometry.id}
                  geometry={geometry}
                  taskId={task.id}
                  isSelected={isSelected}
                  zoom={zoom}
                />
              );

            case GeometryType.Shape:
              return (
                <ShapeRenderer
                  key={geometry.id}
                  geometry={geometry}
                  taskId={task.id}
                  isSelected={isSelected}
                  zoom={zoom}
                />
              );

            case GeometryType.Marker:
              // Markers handled by UnifiedAnchorSymbolLayer
              return null;

            default:
              return null;
          }
        });
      })}
    </>
  );
});
